import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bot, Send, Loader2, Phone, ShieldAlert, CheckCircle2, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/apiClient";

const GREETING = "Hi, I'm the T5 Care Concierge. I'm here 24/7 to help you arrange care for your loved one. Tell me what's going on — for example: \"I need someone to help my mother Monday through Friday.\"";

export default function Concierge() {
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [done, setDone] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((p) => [...p, { role: "user", content: msg }]);
    setInput(""); setSending(true);
    try {
      const { data } = await api.post("/concierge/message", { message: msg, session_id: sessionId, history });
      if (data.session_id) setSessionId(data.session_id);
      setMessages((p) => [...p, { role: "assistant", content: data.reply, emergency: data.emergency }]);
      if (data.lead_created) setDone(true);
    } catch {
      setMessages((p) => [...p, { role: "assistant", content: "I'm having trouble connecting right now. Please call us at (614) 555-0123 and our team will help right away." }]);
    } finally { setSending(false); }
  };

  return (
    <div className="bg-gradient-to-b from-blue-950 to-blue-900 text-white min-h-screen" data-testid="concierge-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center">
          <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-teal-500/20 text-teal-300 mb-3"><Bot className="w-7 h-7" /></span>
          <div className="flex items-center justify-center gap-2 mb-1"><Heart className="w-4 h-4 text-teal-400" /><p className="text-xs uppercase tracking-[0.18em] text-teal-400 font-semibold">T5 Care Concierge · Available 24/7</p></div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold">Let's find the right care for your family</h1>
          <p className="text-blue-200/70 mt-2 max-w-xl mx-auto">Answer a few simple questions and a T5 care coordinator will follow up within one business day to schedule a free consultation.</p>
        </div>

        <div className="mt-8 bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="p-5 space-y-4 max-h-[52vh] overflow-y-auto" data-testid="concierge-thread">
            {messages.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`concierge-msg-${m.role}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-blue-900 text-white" : m.emergency ? "bg-red-50 border border-red-200 text-red-800" : "bg-slate-100 text-slate-800"}`}>
                  {m.emergency && <p className="flex items-center gap-1.5 font-semibold mb-1"><ShieldAlert className="w-4 h-4" /> Emergency</p>}
                  {m.content}
                </div>
              </motion.div>
            ))}
            {sending && <div className="flex justify-start"><div className="bg-slate-100 rounded-2xl px-4 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-teal-600" /></div></div>}
            <div ref={endRef} />
          </div>

          {done && (
            <div className="mx-5 mb-3 bg-teal-50 border border-teal-200 rounded-xl p-3 flex items-center gap-2 text-teal-800 text-sm" data-testid="concierge-done">
              <CheckCircle2 className="w-5 h-5" /> Your request is in — a T5 coordinator will reach out within one business day.
            </div>
          )}

          <div className="border-t border-slate-100 p-3 flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your message…" data-testid="concierge-input" className="flex-1 text-slate-900" disabled={sending} />
            <Button onClick={() => send()} disabled={sending} data-testid="concierge-send" className="bg-teal-600 hover:bg-teal-700 gap-1.5"><Send className="w-4 h-4" /> Send</Button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 justify-center">
          {["I need weekday help for my mom", "What services does T5 offer?", "How soon can care start?"].map((s) => (
            <button key={s} onClick={() => send(s)} className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-blue-100 transition-colors" data-testid="concierge-suggestion">{s}</button>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-blue-200/50 flex items-center justify-center gap-1.5">
          <Phone className="w-3.5 h-3.5" /> In an emergency call 911. T5 provides non-medical in-home care · <Link to="/request-care" className="underline hover:text-teal-300">Prefer a form?</Link>
        </p>
      </div>
    </div>
  );
}
